import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Heading } from 'components/Heading';
import { MobileScreen } from 'components/MobileScreen';
import { Spacer } from 'components/Spacer';
import { getPhotoUrl, getFileExtension, requireGameLoaded } from 'engine/api';
import { useStore } from 'engine/store';
import { showConfetti } from 'engine/lib';
import { Star, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { TeamRecord } from 'shared/modelTypes/client';

export const Route = createFileRoute('/leaderboard')({
	component: RouteComponent,
	beforeLoad: async (ctx) => {
		await requireGameLoaded(ctx);
		const { game } = useStore.getState();
		if (game?.hideScore) {
			// Don't show leaderboard
			throw redirect({
				to: '/',
			});
		}
	},
});

function RouteComponent() {
	const teams = useStore((state) => state.teams).sort((a, b) => {
		if (b.score !== a.score) return b.score - a.score;
		return a.name.localeCompare(b.name);
	});

	const rankedTeams = getRankings(teams);

	// group by place
	const groupedTop3: Record<number, TeamRecord[]> = {};
	rankedTeams.forEach(({ team, place }) => {
		if (place <= 3) {
			if (!groupedTop3[place]) groupedTop3[place] = [];
			groupedTop3[place].push(team);
		}
	});

	return (
		<MobileScreen>
			<div className="sticky top-0 z-10 flex h-1/2 flex-col pt-0 skin-bg">
				<Heading mode="primary">{__`Leaderboard`}</Heading>

				{/* Podium Section */}
				<div className="scale-80 mt-4 flex shrink-0 items-end justify-center gap-6 pb-3">
					{/* Left: second place (only if no first-place tie) */}
					{groupedTop3[2]?.[0] && groupedTop3[1]?.length === 1 && <PodiumCard team={groupedTop3[2][0]} place={2} />}

					{/* Middle: first place */}
					<div className="flex flex-col items-center">
						{groupedTop3[1]?.length > 1 ? (
							// When tied, show ONE stretched podium with all tied teams
							<PodiumCard teamGroup={groupedTop3[1]} place={1} isMainPodium tiedCount={groupedTop3[1].length} />
						) : (
							<PodiumCard team={groupedTop3[1]?.[0]} place={1} isMainPodium />
						)}
					</div>

					{/* Right: always display */}
					{groupedTop3[2]?.[1] ? (
						<PodiumCard team={groupedTop3[2][1]} place={2} />
					) : groupedTop3[3]?.[0] ? (
						<PodiumCard team={groupedTop3[3][0]} place={3} />
					) : (
						// fallback empty slot (keeps layout stable)
						<div className="w-16" />
					)}
				</div>
			</div>

			{/* Full list */}
			<div className="flex h-full flex-col">
				<div className="relative z-0 flex flex-col gap-4 overflow-y-auto">
					{rankedTeams.map(({ team, place }) => (
						<TeamRow key={team.id} place={place} team={team} />
					))}
				</div>
			</div>
		</MobileScreen>
	);
}

function getRankings(teams: TeamRecord[]): { team: TeamRecord; place: number }[] {
	if (!teams.length) return [];

	let place = 1;
	return teams.map((team, index) => {
		if (index === 0) {
			return { team, place };
		}

		// if this team has the same score as the previous one, same place
		if (team.score === teams[index - 1].score) {
			return { team, place };
		}

		// otherwise, rank is index+1
		place = index + 1;
		return { team, place };
	});
}

function TeamRow({ place, team }: { place: number; team: TeamRecord }) {
	const navigate = useNavigate();

	const eventId = useStore((s) => s.eventId);
	const teamId = useStore((s) => s.teamId);
	const hideScore = useStore((s) => s.game?.hideScore);

	const isCurrentTeam = team.id === teamId;
	const rowRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (hideScore) {
			navigate({ to: '/' });
		}
	}, [hideScore, navigate]);

	useEffect(() => {
		if (isCurrentTeam && rowRef.current) {
			rowRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	}, [isCurrentTeam]);

	if (isCurrentTeam && place <= 3) {
		showConfetti();
		new Audio('/sfx/tata-fanfare.mp3').play().catch((err) => console.warn('Could not play notification sound:', err));
	}

	return (
		<div
			ref={rowRef}
			className={`h-[35%] ${
				isCurrentTeam
					? 'flex items-center gap-2 rounded-full p-1.5 px-3 skin-bg-button'
					: 'bg-gray-0 flex items-center gap-2 rounded-full p-1.5 px-3'
			} `}
		>
			<span className="w-3 text-right text-sm">{place}</span>
			{team.data?.avatar ? (
				<img
					src={getPhotoUrl(team.data.avatar.id, getFileExtension(team.data.avatar.filename)!, team.id, eventId!)}
					alt="avatar"
					className="size-8 rounded-full bg-black object-cover"
				/>
			) : (
				<div className="flex size-8 items-center justify-center rounded-full skin-bg-badge">
					<User className="size-5 opacity-70 skin-text-badge" />
				</div>
			)}
			<strong className={isCurrentTeam ? 'text-2xl' : ''}>{team.name}</strong>
			<Spacer />
			<span className="w-12 text-right">
				<CountUp target={team.score} />
			</span>
		</div>
	);
}

const CountUp = ({ target }: { target: number }) => {
	const [count, setCount] = useState(0);

	useEffect(() => {
		let start = 0;
		const step = Math.ceil(target / 40);
		const interval = setInterval(() => {
			start += step;
			if (start >= target) {
				setCount(target);
				clearInterval(interval);
			} else {
				setCount(start);
			}
		}, 30);
		return () => clearInterval(interval);
	}, [target]);

	return <span>{count}</span>;
};

function PodiumCard({
	team,
	teamGroup,
	place,
	isMainPodium = false,
	tiedCount = 1,
}: {
	team?: TeamRecord;
	teamGroup?: TeamRecord[];
	place: number;
	isMainPodium?: boolean;
	tiedCount?: number;
}) {
	const eventId = useStore((s) => s.eventId);
	const teamId = useStore((s) => s.teamId);

	const podiumHeights = {
		1: 'h-32 bg-yellow-300',
		2: 'h-24 bg-gray-300',
		3: 'h-20 bg-yellow-600',
	};

	// If main podium (1st place) and tied, stretch width
	const podiumWidth =
		isMainPodium && place === 1 && tiedCount > 1
			? 'w-full' // enough to overlap left podium space
			: 'w-16';

	// If we received a group (tied teams), render all inside one block
	const renderTeams = teamGroup ?? (team ? [team] : []);

	return (
		<div className="flex flex-col items-center">
			<div className="flex justify-center gap-4">
				{renderTeams.map((t) => {
					const isCurrentTeam = t.id === teamId;
					return (
						<div key={t.id} className="flex flex-col items-center">
							{t.data?.avatar ? (
								<img
									src={getPhotoUrl(t.data?.avatar.id, getFileExtension(t.data?.avatar.filename)!, t.id, eventId!)}
									alt="avatar"
									className="size-16 rounded-full border-2 border-white shadow"
								/>
							) : (
								<div className="flex size-16 items-center justify-center rounded-full bg-gray-200">
									<User className="size-8 text-gray-500" />
								</div>
							)}
							<p className={`mt-2 text-wrap ${isCurrentTeam ? 'text-2xl font-extrabold' : 'text-sm font-medium'}`}>
								{t.name}
							</p>
							<p
								className={`items-bottom flex justify-center gap-1 text-yellow-600 ${
									isCurrentTeam ? 'text-lg font-bold' : 'text-base'
								}`}
							>
								<Star className="size-5 fill-yellow-500 text-yellow-500" />
								{t.score}
							</p>
						</div>
					);
				})}
			</div>

			{/* Shared Podium Block */}
			<div className={`mt-1 flex items-center justify-center rounded-xl ${podiumHeights[place as 1 | 2 | 3]} ${podiumWidth}`}>
				<span className="text-stroke text-2xl font-bold text-white">{place}</span>
			</div>
		</div>
	);
}
